<template>
	<transition
		enter-active-class="transition-all duration-3s timing-ease"
		leave-active-class="transition-all duration-3s timing-ease"
		enter-class="opacity-0"
		enter-to-class="opacity-1"
		leave-class="opacity-1"
		leave-to-class="opacity-0"
		@after-enter="showContent = true"
		@before-leave="showContent = false"
	>
		<div
			class="fixed inset-0 bg-white flex flex-col overflow-auto"
			style="z-index:999999;"
			v-if="isVisible"
			ref="checkoutWindow"
		>
			<div
				class="bg-top text-top-text p-8 flex-shrink-0"
				v-if="showContent"
			>
				<div class="container mx-auto">
					<div class="flex items-center">
						<div class="mr-auto">
							<div v-if="!logoUrl">{{ logoAlt }}</div>
							<img
								v-if="logoUrl"
								:src="logoUrl"
								:alt="logoAlt"
								style="max-width:190px;max-height:95px"
							/>
						</div>
						<button
							class="bg-transparent border-0 p-2 text-top-text inline-flex items-center"
							@click="close"
							v-if="!receiptContent"
						>
							<span class="font-bold mr-4">Tilbage til kurven</span>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								viewBox="0 0 12 12"
								class="w-3 fill-current"
							>
								<path
									fill-rule="evenodd"
									d="M6 7l-4 5-2-2 5-4-5-4 2-2 4 5 4-5 2 2-5 4 5 4-2 2-4-5z"
									clip-rule="evenodd"
								/>
							</svg>
						</button>
					</div>
				</div>
			</div>

			<div class="mb-auto text-black flex-shrink-0" v-if="showContent">
				<div class="container mx-auto" v-if="receiptContent">
					<div v-html="receiptContent"></div>
				</div>
				<div class="container mx-auto" v-else>
					<div
						class="hidden mt-4 md:mt-6 lg:mt-10 md:mx-auto md:w-9/12 md:flex items-center content-center justify-center"
					>
						<div class="flex text-gray-700 font-bold items-center">
							<div
								class="rounded-full bg-gray-700 text-white w-6 h-6 mr-3 text-center items-center justify-center flex"
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									class="fill-current h-2"
									viewBox="0 0 12 9"
								>
									<path d="M4 6l7-6 1 1-8 7-3-4 1-1 2 3z" />
								</svg>
							</div>
							<span>Vælg arrangementer</span>
						</div>

						<div
							class="mx-8 border-t border-gray-300 flex-grow"
						></div>

						<div class="flex text-top font-bold items-center">
							<div
								class="rounded-full bg-top text-white w-6 h-6 mr-2 flex items-center justify-center"
							>
								<span class="text-xs">2</span>
							</div>
							<span>Deltageroplysninger</span>
						</div>

						<div
							class="mx-8 border-t border-gray-300 flex-grow"
						></div>

						<div class="flex text-black font-bold items-center">
							<div
								class="rounded-full bg-white border border-gray-300 w-6 h-6 mr-2 flex items-center justify-center"
							>
								<span class="text-xs">3</span>
							</div>
							<span>Betaling</span>
						</div>
					</div>

					<div class="flex mt-4 md:mt-6 lg:mt-10">
						<div class="md:w-8/12 pb-12">
							<div
								class="border border-gray-300 border-dashed text-gray-900 text-base p-2 mb-4"
								v-if="basket.Now && basket.Expires"
							>
								Tid til at gennemføre ordren:
								<Countdown
									class="font-bold"
									:now="basket.LocalNow"
									:expires="basket.LocalExpires"
								/>
							</div>

							<mobile-pay-checkout
								v-if="isMobilePayCheckout"
								:mobile-pay-data="basket.MobilePayData"
								:association-guid="associationGuid"
								:card-logos="cardLogos"
							/>
							<member-checkout
								v-if="isMemberCheckout"
								:member-data="basket.MemberData"
								:association-guid="associationGuid"
							/>
							<quick-fill
								:order-lines="basket.OrderLines"
								:association-guid="associationGuid"
								v-if="isRegularCheckout"
							></quick-fill>

							<order-line
								v-for="(ol, index) in basket.OrderLines"
								:key="ol.ProductId"
								:order-line="ol"
								:index="index"
							></order-line>
							<payment-form
								:card-logos="cardLogos"
								v-if="
									!isMobilePayCheckout &&
										basket.TotalPrice > 0 &&
										(!isMemberCheckout ||
											(basket.MemberData &&
												basket.MemberData.AcceptedFromBackend))
								"
							></payment-form>
							<billing-form v-if="isRegularCheckout"></billing-form>

							<confirm-order
								:terms-link="termsLink"
								:association-guid="associationGuid"
							></confirm-order>

							<div class="px-4 sm:px-0">
									<button @click="goToPayment()"
												class="mt-6 py-4 px-6 block w-full text-center rounded text-base font-bold text-white bg-top"
													:class="{'opacity-25 cursor-default' : goToPaymentDisabled}"
										:disabled="isCheckingOut">
											<span v-if="!isCheckingOut">Gå til betaling</span>
											<spinner v-if="isCheckingOut"></spinner>
									</button>
							</div>

							<transition
								enter-active-class="transition-all duration-3s timing-ease"
								leave-active-class="transition-all duration-3s timing-ease"
								enter-class="opacity-0 scale-y-0 -translate-y-full"
								enter-to-class="opacity-1 scale-y-100 translate-y-0"
								leave-class="opacity-1 scale-y-100 translate-y-0"
								leave-to-class="opacity-0 scale-y-0 -translate-y-full"
							>
								<div
									v-if="hasTriedToCheckout && missingFieldCount > 0"
									class="mt-2 flex items-center justify-center text-red-700 text-center"
								>
									<svg
										width="18"
										height="18"
										fill="currentColor"
										class="mr-2"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											fill-rule="evenodd"
											clip-rule="evenodd"
											d="M9 .667A8.336 8.336 0 00.667 9c0 4.6 3.733 8.333 8.333 8.333S17.334 13.6 17.334 9 13.6.667 9 .667zm-.833 12.5V11.5h1.667v1.666H8.167zm0-8.334v5h1.667v-5H8.167z"
										/>
									</svg>
									Du mangler at udfylde {{ missingFieldCount }} felt{{
										missingFieldCount > 1 ? "er" : ""
									}}
								</div>
							</transition>
						</div>
						<div class="hidden md:block w-4/12 ml-8">
							<div class="border border-gray-200 sticky inset-y-0">
								<div class="border-b border-gray-200 p-5 text-lg">
									Ordreoversigt
								</div>
								<div class="p-5">
									<div class="flex justify-between">
										<div>Subtotal</div>
										<div>{{ basket.TotalPriceWithoutVAT | toCurrency }}</div>
									</div>
									<div class="flex justify-between">
										<div>Moms</div>
										<div>{{ basket.TotalVAT | toCurrency }}</div>
									</div>
									<div
										class="flex justify-between"
										v-if="basket.TotalDiscount > 0"
									>
										<div>Rabat</div>
										<div>{{ -basket.TotalDiscount | toCurrency }}</div>
									</div>
									<div class="flex justify-between font-bold">
										<div>Total</div>
										<div>{{ basket.TotalPrice | toCurrency }}</div>
									</div>

									<button
										@click="goToPayment()"
										class="mt-6 py-2 block w-full text-center rounded text-base font-bold text-white bg-top"
													:class="{'opacity-25 cursor-default' : goToPaymentDisabled}"
										:disabled="isCheckingOut"
									>
										<span v-if="!isCheckingOut">Gå til betaling</span>
										<spinner v-if="isCheckingOut"></spinner>
									</button>

									<transition
										enter-active-class="transition-all duration-3s timing-ease"
										leave-active-class="transition-all duration-3s timing-ease"
										enter-class="opacity-0 scale-y-0 -translate-y-full"
										enter-to-class="opacity-1 scale-y-100 translate-y-0"
										leave-class="opacity-1 scale-y-100 translate-y-0"
										leave-to-class="opacity-0 scale-y-0 -translate-y-full"
									>
										<div
											v-if="hasTriedToCheckout && missingFieldCount > 0"
											class="mt-2 flex items-center justify-center text-red-700 text-center"
										>
											<svg
												width="18"
												height="18"
												fill="currentColor"
												class="mr-2"
												xmlns="http://www.w3.org/2000/svg"
											>
												<path
													fill-rule="evenodd"
													clip-rule="evenodd"
													d="M9 .667A8.336 8.336 0 00.667 9c0 4.6 3.733 8.333 8.333 8.333S17.334 13.6 17.334 9 13.6.667 9 .667zm-.833 12.5V11.5h1.667v1.666H8.167zm0-8.334v5h1.667v-5H8.167z"
												/>
											</svg>
											Du mangler at udfylde {{ missingFieldCount }} felt{{
												missingFieldCount > 1 ? "er" : ""
											}}
										</div>
									</transition>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<footer v-if="showContent" class="checkout--footer flex-shrink-0">
				<slot></slot>
			</footer>
		</div>
	</transition>
</template>

<style>
.checkout--footer .contact-info h3 {
	color: currentColor;
}
</style>

<script>
import PaymentForm from "./PaymentForm";
import BillingForm from "./BillingForm";
import OrderLine from "./OrderLine";
import ConfirmOrder from "./ConfirmOrder";
import QuickFill from "./QuickFill";
import MobilePayCheckout from "./MobilePayCheckout";
import MemberCheckout from "./MemberCheckout";
import Countdown from "./Countdown";

export default {
	data() {
		return {
			showContent: false
		};
	},
	components: {
		PaymentForm,
		BillingForm,
		OrderLine,
		Countdown,
		ConfirmOrder,
		QuickFill,
		MobilePayCheckout,
		MemberCheckout
	},
	props: {
		logoUrl: String,
		logoAlt: String,
		nodeId: Number,
		associationGuid: String,
		termsLink: String,
		cardLogos: Object
	},
	computed: {
		missingFieldCount: function() {
			return this.$store.getters.missingFieldCount;
		},
		hasTriedToCheckout: function() {
			return this.$store.getters.hasTriedToCheckout;
		},
		receiptContent: function() {
			return this.$store.getters.receiptContent;
		},
		basket: function() {
			return this.$store.getters.basket;
		},
		isVisible: function() {
			return this.$store.getters.checkoutVisible;
		},
		isCheckingOut: function() {
			return this.$store.getters.isCheckingOut;
		},
		goToPaymentDisabled: function() {
			if (
				this.checkoutState == "mobilePay" &&
				this.basket.MobilePayData.Accepted &&
				(!this.basket.MobilePayData.ConfirmedNumber ||
					this.basket.MobilePayData.ConfirmedNumber == "" ||
					this.basket.MobilePayData.ConfirmedNumber !=
						this.basket.MobilePayData.Mobile)
			) {
				return false;
			}

			if (
				this.checkoutthis == "member" &&
				this.basket.MemberData.Accepted &&
				(!this.basket.MemberData.ConfirmedDiscountCode ||
					this.basket.MemberData.DiscountId == 0 ||
					this.basket.MemberData.ConfirmedDiscountCode !=
						this.basket.MemberData.DiscountCode)
			) {
				return false;
			}
			return !this.$store.getters.allowGoToPayment;
		},
		checkoutState: function() {
			return this.$store.getters.checkoutState;
		},
		isMobilePayCheckout: function() {
			return this.checkoutState == "mobilePay";
		},
		isMemberCheckout: function() {
			return this.checkoutState == "member";
		},
		isRegularCheckout: function() {
			return !this.isMobilePayCheckout && !this.isMemberCheckout;
		}
	},
	methods: {
		goToPayment: function() {
			this.$store.dispatch("resetTryingToCheckout");
			this.$store.dispatch("tryingToCheckout");
			if (!this.goToPaymentDisabled) {
				this.$store
					.dispatch("placeOrder", { basket: this.basket, nodeId: this.nodeId })
					.then(() => {
						this.$refs.checkoutWindow.scrollTo(0, 0);
					});
			}
		},
			close: function () {
					document.querySelector("#checkoutwhiteout")?.remove();
			this.$store.dispatch("resetTryingToCheckout");
			this.$store.dispatch("toggleCheckout");
			if (this.$store.getters.basket.MemberData.Accepted) {
				this.$store.getters.basket.MemberData.Accepted = false;
				this.$store.dispatch("saveBasketNow", this.basket).then(() => {});
			}
		}
	}
};
</script>
